import {
  getProjectCostBandRows,
  type ProjectCostLang,
  type ProjectCostProfile,
} from '@/lib/projectCostEstimate';

const TABLE_CLASS =
  'w-full min-w-[520px] text-left border border-border/70 rounded-xl overflow-hidden text-sm';
const TH_CLASS =
  'font-body text-xs uppercase tracking-wider p-3 sm:p-4 text-muted-foreground border-b border-border/50';
const TD_CLASS = 'font-body text-sm p-3 sm:p-4 text-foreground/90 border-b border-border/40';
const THEAD_CLASS = 'bg-card/60';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildTableHtml(headers: string[], rows: string[][]): string {
  const body = rows
    .map((cells) => `<tr>${cells.map((cell) => `<td class="${TD_CLASS}">${escapeHtml(cell)}</td>`).join('')}</tr>`)
    .join('');

  return `<table class="${TABLE_CLASS}"><thead class="${THEAD_CLASS}"><tr>${headers
    .map((h) => `<th class="${TH_CLASS}">${escapeHtml(h)}</th>`)
    .join('')}</tr></thead><tbody>${body}</tbody></table>`;
}

export function buildCostBandsTableHtml(profile: ProjectCostProfile, lang: ProjectCostLang): string {
  const rows = getProjectCostBandRows(profile, lang);
  const headers =
    lang === 'tr'
      ? ['Kalem', 'TRY (KDV hariç)', 'Süre']
      : ['Line item', 'TRY (excl. VAT)', 'Timeline'];
  return buildTableHtml(
    headers,
    rows.map((row) => [row.scopeLabel, row.range, row.duration]),
  );
}

export function buildSaasCostBandsTableHtml(lang: ProjectCostLang): string {
  return buildCostBandsTableHtml('saas_mvp', lang);
}

export function buildSaasPhaseDecisionTableHtml(lang: ProjectCostLang): string {
  const rows = getProjectCostBandRows('saas_mvp', lang);
  const mvp = rows[0];
  const platform = rows[1];
  const platformFloor = platform.range.split('–')[0];

  const phases =
    lang === 'tr'
      ? [
          ['MVP', 'Tek kritik akış + ölçüm', mvp.range, mvp.duration],
          ['v1', 'Billing, rol yönetimi, temel admin', platform.range, platform.duration],
          [
            'Scale',
            'Multi-tenant, gözlemlenebilirlik, performans',
            `${platformFloor}+ (kapsama göre)`,
            `${platform.duration}+`,
          ],
        ]
      : [
          ['MVP', 'One critical flow + measurement', mvp.range, mvp.duration],
          ['v1', 'Billing, roles, basic admin', platform.range, platform.duration],
          [
            'Scale',
            'Multi-tenant, observability, performance',
            `${platformFloor}+ (scope-dependent)`,
            `${platform.duration}+`,
          ],
        ];

  const headers =
    lang === 'tr'
      ? ['Faz', 'Tipik kapsam', 'Örnek bütçe bandı', 'Süre']
      : ['Phase', 'Typical scope', 'Illustrative budget band', 'Timeline'];

  return buildTableHtml(headers, phases);
}

export function buildSaasBandScopeTableHtml(lang: ProjectCostLang): string {
  const rows = getProjectCostBandRows('saas_mvp', lang);
  const headers =
    lang === 'tr'
      ? ['Bant', 'Dahil', 'Hariç (ayrı kapsamlanır)']
      : ['Band', 'Included', 'Excluded (scoped separately)'];

  const scope =
    lang === 'tr'
      ? [
          [
            'Tek kritik kullanıcı akışı, kimlik doğrulama, temel rol yapısı, temel admin, ürün analitiği/ölçüm, staging + production kurulumu',
            'Üçüncü taraf entegrasyonlar (ödeme, SSO, ERP/CRM), veri migrasyonu, KVKK/GDPR uyum çalışması, ileri QA otomasyonu, DevOps/SLA',
          ],
          [
            'Abonelik/billing akışı, rol ve yetki yönetimi, gelişmiş admin, raporlama, multi-tenant veri modeli',
            'Ödeme sağlayıcı ve kurumsal entegrasyonlar, veri migrasyonu, uyum denetimleri, yük testi, 7/24 operasyon',
          ],
          [
            'Kullanıcı akışları, wireframe, UI tasarımı, temel tasarım sistemi',
            'Marka kimliği, illüstrasyon/motion, kapsamlı kullanıcı araştırması',
          ],
          [
            'Hata düzeltme, güvenlik ve bağımlılık güncellemeleri, küçük iyileştirmeler',
            'Yeni özellik geliştirme, 7/24 on-call/SLA, bulut altyapı faturaları',
          ],
        ]
      : [
          [
            'One critical user flow, authentication, basic roles, basic admin, product analytics/measurement, staging + production setup',
            'Third-party integrations (payments, SSO, ERP/CRM), data migration, GDPR/KVKK compliance work, advanced QA automation, DevOps/SLA',
          ],
          [
            'Subscription/billing flow, role and permission management, advanced admin, reporting, multi-tenant data model',
            'Payment provider and enterprise integrations, data migration, compliance audits, load testing, 24/7 operations',
          ],
          [
            'User flows, wireframes, UI design, basic design system',
            'Brand identity, illustration/motion, extensive user research',
          ],
          [
            'Bug fixes, security and dependency updates, small improvements',
            'New feature development, 24/7 on-call/SLA, cloud infrastructure bills',
          ],
        ];

  return buildTableHtml(
    headers,
    rows.map((row, index) => [row.scopeLabel, scope[index][0], scope[index][1]]),
  );
}

export function buildMobileCostDriversTableHtml(lang: ProjectCostLang): string {
  const headers =
    lang === 'tr'
      ? ['Maliyet sürücüsü', 'Bandın alt ucuna yakın', 'Bandın üst ucuna / üstüne iter']
      : ['Cost driver', 'Closer to the lower end', 'Pushes to the upper end / beyond'];

  const rows =
    lang === 'tr'
      ? [
          [
            'Platform sayısı',
            'Tek platform veya tek kod tabanıyla iOS + Android',
            'iOS + Android + tablet/web paneli ayrı deneyimler',
          ],
          [
            'Native vs cross-platform',
            'React Native / Flutter, standart UI bileşenleri',
            'Ayrı native kod tabanları veya yoğun native modül (kamera, BLE, ağır animasyon)',
          ],
          [
            'Backend',
            'Mevcut API veya hazır backend servisi üzerine',
            'Sıfırdan backend API + admin panel (toplam maliyetin %40–60’ına çıkabilir)',
          ],
          [
            'Entegrasyonlar',
            'Push, analitik, crash reporting',
            'Ödeme, harita/konum, cihaz/IoT, kurumsal SSO — her biri ayrı kapsamlanır',
          ],
          [
            'Offline ve senkronizasyon',
            'Online-only akışlar',
            'Offline-first, çakışma çözümü, arka plan senkronizasyonu',
          ],
        ]
      : [
          [
            'Platform count',
            'One platform, or iOS + Android from a single codebase',
            'iOS + Android + tablet/web panel as separate experiences',
          ],
          [
            'Native vs cross-platform',
            'React Native / Flutter with standard UI components',
            'Separate native codebases or heavy native modules (camera, BLE, rich animation)',
          ],
          [
            'Backend',
            'Built on an existing API or a managed backend service',
            'New backend API + admin panel (can reach 40–60% of total cost)',
          ],
          [
            'Integrations',
            'Push, analytics, crash reporting',
            'Payments, maps/location, device/IoT, enterprise SSO — each scoped separately',
          ],
          [
            'Offline and sync',
            'Online-only flows',
            'Offline-first, conflict resolution, background sync',
          ],
        ];

  return buildTableHtml(headers, rows);
}

export function saasCostGuideMethodologyCaption(lang: ProjectCostLang): string {
  return lang === 'tr'
    ? 'Tablodaki TRY aralıkları ücretsiz proje maliyet tahmincisi ile aynıdır (premium butik bandlar, KDV hariç). Entegrasyon, veri migrasyonu, uyumluluk, QA ve DevOps ayrı kapsamlanır; bantlar sabit fiyat teklifi değildir, kesin fiyat yazılı teklifte hesaplanır.'
    : 'TRY ranges match the free project cost estimator (premium boutique bands, excl. VAT). Integrations, data migration, compliance, QA, and DevOps are scoped separately; bands are not a fixed quote — the final price is calculated in a written proposal.';
}

export function mobileCostGuideMethodologyCaption(lang: ProjectCostLang): string {
  return lang === 'tr'
    ? 'KDV hariç, örnek bant, kesin fiyat teklifte. TRY aralıkları ücretsiz proje maliyet tahmincisinin “mobil ürün” profiliyle aynıdır (premium butik bandlar). Entegrasyon, veri migrasyonu, uyumluluk, QA ve DevOps ayrı kapsamlanır.'
    : 'Excl. VAT, illustrative band, final price in the proposal. TRY ranges match the free project cost estimator’s “mobile product” profile (premium boutique bands). Integrations, data migration, compliance, QA, and DevOps are scoped separately.';
}
